import axios from 'axios';

export interface Review {
  id: string;
  author: string;
  rating: number;
  title?: string;
  content: string;
  date: string;
  source: 'trustpilot';
}

// Trustpilot - Fetch using multiple proxy strategies
async function getTrustpilotReviews(): Promise<Review[]> {
  // Try multiple proxy services in order
  const proxies = [
    (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    (url: string) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
    (url: string) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
  ];
  
  const targetUrl = 'https://www.trustpilot.com/review/www.ding.com';
  
  for (let i = 0; i < proxies.length; i++) {
    try {
      const proxyUrl = proxies[i](targetUrl);
      console.log(`Fetching from Trustpilot via proxy ${i + 1}/${proxies.length}...`);
      
      const response = await axios.get(proxyUrl, {
        timeout: 10000,
        headers: {
          'Accept': 'text/html',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });
      
      if (!response.data) {
        console.error(`No data from proxy ${i + 1}`);
        continue;
      }
      
      const html = typeof response.data === 'string' ? response.data : String(response.data);
      console.log(`Received HTML from proxy ${i + 1}, length:`, html.length);
      
      // Try to find the Next.js data or LD+JSON
      // First try: __NEXT_DATA__ script tag (Trustpilot uses Next.js)
      const nextDataMatch = html.match(/<script id="__NEXT_DATA__"[^>]*>(.*?)<\/script>/s);
      if (nextDataMatch && nextDataMatch[1]) {
        try {
          const nextData = JSON.parse(nextDataMatch[1]);
          console.log('Found __NEXT_DATA__');
          
          // Navigate the Next.js data structure
          const pageProps = nextData?.props?.pageProps;
          if (pageProps?.reviews) {
            const reviews = pageProps.reviews.map((review: any, index: number) => ({
              id: `trustpilot-${review.id || index}`,
              author: review.consumer?.displayName || 'Anonymous',
              rating: review.rating || 5,
              title: review.title || '',
              content: review.text || '',
              date: review.dates?.publishedDate || new Date().toISOString(),
              source: 'trustpilot' as const
            }));
            console.log(`Parsed ${reviews.length} reviews from __NEXT_DATA__`);
            return reviews;
          }
        } catch (e) {
          console.error('Error parsing __NEXT_DATA__:', e);
        }
      }
      
      // Fallback: Try LD+JSON
      const jsonMatches = html.matchAll(/<script[^>]*type="application\/ld\+json"[^>]*>(.*?)<\/script>/gs);
      for (const match of jsonMatches) {
        try {
          const data = JSON.parse(match[1]);
          if (data['@type'] === 'Product' && data.review && Array.isArray(data.review)) {
            const reviews = data.review.map((review: any, index: number) => ({
              id: `trustpilot-${index}`,
              author: review.author?.name || 'Anonymous',
              rating: review.reviewRating?.ratingValue || 5,
              title: review.name || '',
              content: review.reviewBody || review.description || '',
              date: review.datePublished || new Date().toISOString(),
              source: 'trustpilot' as const
            }));
            console.log(`Parsed ${reviews.length} reviews from LD+JSON`);
            return reviews;
          }
        } catch (e) {
          // Try next script tag
          continue;
        }
      }
      
      console.log(`No reviews found in proxy ${i + 1} response`);
    } catch (error) {
      console.error(`Error with proxy ${i + 1}:`, error);
      // Continue to next proxy
      continue;
    }
  }
  
  console.log('All proxies failed, returning empty array');
  return [];
}

// Get reviews from Trustpilot only
export async function getAllReviews(): Promise<Review[]> {
  try {
    console.log('Fetching reviews from Trustpilot...');
    
    const trustpilotReviews = await getTrustpilotReviews();
    
    console.log(`Fetched ${trustpilotReviews.length} Trustpilot reviews`);
    
    // Shuffle and return
    return trustpilotReviews.sort(() => Math.random() - 0.5);
  } catch (error) {
    console.error('Error fetching Trustpilot reviews:', error);
    return [];
  }
}

export const getReviews = getAllReviews;
